// A commissioner adding or taking points for one team in one week
// (COMMISSIONER_OVERRIDES.md, J1). Which team, add or take, how many (to a
// tenth, as a score is shown), a reason — and what the score will become and
// who will be told, said before it is saved.

import { useState } from 'react';
import type { TeamRef } from '@fsp/domain';
import { Sheet } from '../../kit/overlays';
import { tell, Button, Callout, Choice, NumberField, OneOf, Stack, TextArea } from '../../kit/ui';
import { adjustPoints } from '../../data/overrides';

/**
 * Whose points can be adjusted, and which row each one's score is on: a
 * matchup's two teams share its row; in a league with no opponents (Q4, Q5)
 * every team's week is a row of its own.
 */
export interface AdjustTarget {
  period: number;
  sides: { matchupId: string; team: TeamRef; score: number }[];
}

export function AdjustPointsSheet({
  target,
  leagueId,
  opened,
  onClose,
}: {
  target: AdjustTarget;
  leagueId: string;
  opened: boolean;
  onClose: () => void;
}) {
  const [teamId, setTeamId] = useState(target.sides[0]?.team.id ?? '');
  const [way, setWay] = useState<'add' | 'take'>('add');
  const [points, setPoints] = useState<number | null>(null);
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  const side = target.sides.find((s) => s.team.id === teamId) ?? target.sides[0]!;
  // A game's two managers, or the one whose week against the field it is.
  const shared = target.sides.filter((s) => s.matchupId === side.matchupId).length > 1;
  const signed = points === null ? null : way === 'add' ? points : -points;
  const ready = signed !== null && signed !== 0 && reason.trim() !== '' && !saving;
  const after = signed === null ? null : Math.round((side.score + signed) * 10) / 10;

  const close = () => {
    setPoints(null);
    setReason('');
    setProblem(null);
    onClose();
  };

  const save = async () => {
    if (signed === null) return;
    setSaving(true);
    setProblem(null);
    const said = await adjustPoints(leagueId, side.matchupId, {
      teamId,
      points: signed,
      reason: reason.trim(),
    });
    setSaving(false);
    if (!said.ok) return setProblem(said.message);
    close();
    // WHAT COULD NOT BE BROUGHT UP TO DATE after the scores moved, said: the
    // change stands and the managers were told (the review of J1, 1.5).
    if (said.problems.length > 0) tell(said.problems.join(' '), 'warn');
  };

  return (
    <Sheet opened={opened} onClose={close} title={`Adjust points · Week ${target.period}`}>
      <Stack gap={3}>
        {target.sides.length > 2 ? (
          <Choice
            label="Team"
            required
            options={target.sides.map((s) => ({ value: s.team.id, label: s.team.name }))}
            value={teamId}
            onChange={(v) => v && setTeamId(v)}
          />
        ) : (
          <OneOf
            label="Team"
            options={target.sides.map((s) => ({ value: s.team.id, label: s.team.name }))}
            value={teamId}
            onChange={setTeamId}
          />
        )}
        <OneOf
          label="Add or take"
          options={[
            { value: 'add', label: 'Add points' },
            { value: 'take', label: 'Take points' },
          ]}
          value={way}
          onChange={setWay}
        />
        <NumberField
          label="Points"
          value={points}
          onChange={setPoints}
          min={0.1}
          max={999.9}
          step={0.5}
          decimals={1}
          nullable
        />
        <TextArea
          label="Why"
          placeholder="The rule or the reason, in a sentence the league will read"
          value={reason}
          onChange={setReason}
          maxLength={300}
        />
        <Callout>
          {after === null
            ? `${side.team.name}’s score is ${side.score.toFixed(1)}.`
            : `${side.team.name}’s score will be ${after.toFixed(1)} (it is ${side.score.toFixed(1)}).`}{' '}
          The week is scored again, {shared ? 'both managers are' : 'its manager is'} told, and the
          league chat and the commissioner log say so, with your reason. To undo it later, adjust
          the other way.
        </Callout>
        {problem !== null ? <Callout tone="bad">{problem}</Callout> : null}
        <Button onClick={() => void save()} disabled={!ready} loading={saving}>
          Save adjustment
        </Button>
      </Stack>
    </Sheet>
  );
}
